import {useState, useCallback, useEffect} from 'react';
import PropTypes from 'prop-types';
import {TargetTemplateUpload} from '@/components/molecules';
import '@/containers/PerfectStore/PerfectStoreReview/perfectStoreReview.scss';
import PeacockDataTable from '@/components/organisms/PeacockDataTable';
import {useVisitList} from '@/hooks/useVisitList';
import {useAzureBlob} from '@/hooks/useAzureBlob-hook';
import {
  getVisitStatusTemplate,
  updateStoreVisitsTemplate
} from '@/services/perfectStore';
import {getSasToken} from '@/services/sasToken';
import {downloadCSVFiles} from '@/helpers/shadcn/utils';
import {VISIT_STATUS_CSV_RULES} from '@/utils/constants/perfectStoreConstants';
import {Constants} from '@/utils/constants/commonConstants';
import {useAppDispatch} from '@/store';
import {setToastNotifications} from '@/store/slices/commonSlice';
import {logger} from '@/helpers/logger';
import {isWithinDaysBeforeDate} from '@/helpers/dateUtils';

const PAGE_SIZE = 10;

const VISIT_LIST_COLUMNS = [
  {
    name_of_db_column: 'storeId',
    header_of_column: 'STORE ID',
    label_of_column: 'Store ID',
    columnFilterPlaceholder: 'Store ID',
    hideSortIcon: true
  },
  {
    name_of_db_column: 'visitNumber',
    header_of_column: 'VISIT NUMBER',
    label_of_column: 'Visit Number',
    columnFilterPlaceholder: 'Visit Number',
    hideSortIcon: true
  },
  {
    name_of_db_column: 'visitStatusText',
    header_of_column: 'STATUS',
    label_of_column: 'Status',
    columnFilterPlaceholder: 'Status',
    hideSortIcon: true
  }
];

/**
 * UpdateStoreVisitsTab renders the "Update Store Visits" tab content inside
 * the Perfect Store Review page.
 *
 * Provides:
 * - Visit-status CSV download (live API, per targetId)
 * - Upload modal for the updated CSV (reuses TargetTemplateUpload)
 * - Server-side paginated visit-list table (PeacockDataTable)
 *
 * @param {Object}        props
 * @param {string|number} props.targetId       - Perfect Store target ID.
 * @param {Function}      props.onRefetchVisits - Callback to provide refetch function to parent.
 */
const UpdateStoreVisitsTab = ({
  targetId,
  rewardDate,
  activeEndDate,
  awardPointsWhenTargetMet,
  onRefetchVisits
}) => {
  const dispatch = useAppDispatch();
  const {downloadFileOnBlob, uploadFileOnBlob} = useAzureBlob();
  // When "Award Points Immediately" is enabled and no Point Delivery Date
  // exists, use the Active End Date as the upload cutoff so the button is
  // disabled once the target reaches its Active End Date.
  const effectiveCutoffDate =
    awardPointsWhenTargetMet && !rewardDate ? activeEndDate : rewardDate;
  const uploadDisabled = !isWithinDaysBeforeDate(effectiveCutoffDate, 1);

  // UploadFile sets uploadFormData.storeList to the blob URL after a successful upload.
  const [uploadFormData, setUploadFormData] = useState({
    targetMediaFile: [],
    storeList: ''
  });
  const [uploadedFile, setUploadedFile] = useState(null);
  const [sasResponse, setSasResponse] = useState(null);
  const [isPublishing, setIsPublishing] = useState(false);

  const {
    records,
    totalRecords,
    isLoading,
    isFetching,
    pageIndex,
    pageSize,
    setPageIndex,
    handlePageSizeChange,
    setSearchArry,
    handleResetAll,
    refetch
  } = useVisitList(targetId, PAGE_SIZE);

  // Download the existing visit-status CSV from the live API.
  // The API returns { templateUrl, fileName }. We mirror the pattern used by
  // ReviewBulkPointModifiers: extract the blob path, call our backend's
  // getSasToken to get a properly-scoped download token, then fetch via
  // downloadFileOnBlob.
  const handleDownload = useCallback(async () => {
    if (!targetId) return;
    try {
      const response = await getVisitStatusTemplate(targetId);

      const templateUrl = response?.templateUrl ?? response?.data?.templateUrl;
      const fileName = response?.fileName ?? response?.data?.fileName;

      if (!templateUrl) {
        throw new Error('Missing templateUrl in API response');
      }

      const blobFileName = templateUrl.split('/').pop();
      const directory = templateUrl.split('/input')[1];

      const downloadSasResponse = await getSasToken(
        blobFileName,
        Constants.PerfectStore
      );
      const result = await downloadFileOnBlob(
        downloadSasResponse,
        directory,
        blobFileName
      );

      if (!result?.responseText) {
        throw new Error('Empty content returned from blob download');
      }

      downloadCSVFiles(result.responseText, fileName || blobFileName);
    } catch (error) {
      logger.error('Error downloading visit status template:', error);
      dispatch(
        setToastNotifications({
          show: true,
          message:
            'Failed to download visit status template. Please try again.',
          type: 'error'
        })
      );
    }
  }, [targetId, dispatch, downloadFileOnBlob]);

  // Custom upload handler that gets SAS token but doesn't upload to blob yet
  const handleCustomUpload = useCallback(
    async (file, setFileError) => {
      if (!isWithinDaysBeforeDate(effectiveCutoffDate, 1)) return;
      try {
        const sasTokenResponse = await getSasToken(
          file.name,
          Constants.PerfectStore
        );

        if (sasTokenResponse?.blobUrl && sasTokenResponse?.sasToken) {
          setUploadedFile(file);
          setSasResponse(sasTokenResponse);
          setUploadFormData((prev) => ({
            ...prev,
            targetMediaFile: [file.name],
            storeList: sasTokenResponse.blobUrl
          }));
        } else {
          setFileError('Failed to get upload URL. Please try again.');
        }
      } catch (error) {
        logger.error('Error getting SAS token:', error);
        setFileError('Failed to get upload URL. Please try again.');
      }
    },
    [effectiveCutoffDate]
  );

  // Custom handler for the Upload button - performs blob upload + API call
  const handleCustomUploadAction = useCallback(async () => {
    if (
      !isWithinDaysBeforeDate(effectiveCutoffDate, 1) ||
      !targetId ||
      !uploadedFile ||
      !sasResponse
    )
      return;

    setIsPublishing(true);
    try {
      // Upload file to Azure blob using blobUrl and sasToken
      const uploadResult = await uploadFileOnBlob(sasResponse, uploadedFile);

      if (uploadResult?.message === 'Success') {
        // Call update-store-visits-template API with the blobUrl
        const response = await updateStoreVisitsTemplate(targetId, {
          fileUrl: sasResponse.blobUrl
        });

        if (response?.success !== false) {
          // The BFF queues the update and returns {success, message, status:
          // 'queued'} - surface its message so the user knows to check back.
          dispatch(
            setToastNotifications({
              show: true,
              message:
                response?.message ||
                'Target Visit Status update request submitted successfully.',
              type: 'success'
            })
          );
          // Keep the file/blob info until the modal is closed via the Done button.
          return true;
        }

        dispatch(
          setToastNotifications({
            show: true,
            message:
              response?.message ||
              'Failed to publish update. Please try again.',
            type: 'error'
          })
        );
      } else {
        dispatch(
          setToastNotifications({
            show: true,
            message: 'Failed to upload file to blob storage. Please try again.',
            type: 'error'
          })
        );
      }
    } catch (error) {
      logger.error('Error publishing Update Store Visits:', error);
      dispatch(
        setToastNotifications({
          show: true,
          message: 'Failed to publish update. Please try again.',
          type: 'error'
        })
      );
    } finally {
      setIsPublishing(false);
    }
    return false;
  }, [
    effectiveCutoffDate,
    targetId,
    uploadedFile,
    sasResponse,
    uploadFileOnBlob,
    dispatch
  ]);

  // Reset upload state once the modal is closed (via Done, X, or Cancel).
  const handleUploadModalClose = useCallback(() => {
    setUploadFormData({
      targetMediaFile: [],
      storeList: ''
    });
    setUploadedFile(null);
    setSasResponse(null);
    refetch();
  }, [refetch]);

  // Expose refetch to the parent so it can reload the list after publishing.
  useEffect(() => {
    onRefetchVisits?.(refetch);
  }, [onRefetchVisits, refetch]);

  return (
    <div
      className='update-store-visits-tab'
      data-testid='update-store-visits-tab'>
      {/* ── Section heading ── */}
      <p
        className='ut-section-heading'
        data-testid='update-store-visits-heading'>
        Target Visit Status Update
      </p>

      {/* ── Update Store Visits card + upload modal ── */}
      <TargetTemplateUpload
        formData={uploadFormData}
        setFormData={setUploadFormData}
        cardTitle='Update Store Visits'
        cardClassName='mb-2 rounded-lg border border-solid border-[color:var(--color-border-default)] ut-card'
        uploadModalTitle='Upload Target Visit Status'
        uploadModalSubTitle='Upload updated visit status file'
        uploadButtonLabel='Upload Template'
        uploadDisabled={uploadDisabled}
        uploadFor={Constants.PerfectStore}
        isCsvValidationRequired={true}
        customRules={VISIT_STATUS_CSV_RULES}
        salesTargetId={targetId}
        handleDownloadOverride={handleDownload}
        customUploadFile={handleCustomUpload}
        customUploadAction={handleCustomUploadAction}
        isCustomUploading={isPublishing}
        onCloseModal={handleUploadModalClose}
      />

      {/* ── Visit list section ── */}
      <p
        className='ut-section-heading'
        data-testid='update-store-visits-section-heading'>
        Update
      </p>

      <PeacockDataTable
        columns={VISIT_LIST_COLUMNS}
        records={records}
        isLoading={isLoading}
        isFetching={isFetching}
        pageSize={pageSize}
        totalRecords={totalRecords}
        paginationConfig={{
          pageIndex,
          setPageIndex,
          onRowsPerPageChange: handlePageSizeChange
        }}
        filterConfig={{
          setSearchArry,
          onResetAll: handleResetAll,
          showResetAll: true,
          resetAllPosition: 'left'
        }}
        selectionConfig={{
          hideCheckbox: true
        }}
        displayConfig={{
          noDataTitle: 'No visit records found',
          noDataDescription: 'There are no visit records for this target.'
        }}
      />
    </div>
  );
};

UpdateStoreVisitsTab.propTypes = {
  targetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  rewardDate: PropTypes.string,
  activeEndDate: PropTypes.string,
  awardPointsWhenTargetMet: PropTypes.bool,
  onRefetchVisits: PropTypes.func
};

export default UpdateStoreVisitsTab;
